import _ from 'lodash';
import { Auth } from '@or-sdk/auth';
import { createAuthStore } from '@onereach/auth-ui-module';
import { storeToRefs } from 'pinia';

import { useGlobalVars } from '@/composables';
import { useOneReachConfig } from './use-onereach-config';

interface GlobalAuth {
  $ORAuthAPI: Auth;
  $ORAuthStore: ReturnType<ReturnType<typeof createAuthStore>>;
}

/**
 * We can't create auth API or store instances before receiving config.
 * We cache results so we won't have to re-create them multiple times.
 */
export const useOneReachAuth = () => {
  const $global = useGlobalVars().getVueGlobals() as GlobalAuth;

  const getAPIInstance = () => {
    if (!_.isEmpty($global.$ORAuthAPI)) {
      return $global.$ORAuthAPI;
    }

    const ORConfig = useOneReachConfig().getORConfig();
    $global.$ORAuthAPI = new Auth({
      cookieDomain: ORConfig.COOKIE_DOMAIN,
      cookiePrefix: ORConfig.COOKIE_NAME_PREFIX_NEW,
      authUiUrl: `${ORConfig.SDK_API_URL}/auth/token`,
      allowGuestLogin: Boolean(ORConfig.GUEST_LOGIN),
      userCookieName: `${ORConfig.COOKIE_NAME_PREFIX}_name`,
      userExpireCookieName: `${ORConfig.COOKIE_NAME_PREFIX}_user_expire`,
      multiUserCookieName: `${ORConfig.COOKIE_NAME_PREFIX}_multi`,
      expireInLong: Number(`${ORConfig.COOKIE_EXPIRATION_LONG}`),
      expireInShort: Number(`${ORConfig.COOKIE_EXPIRATION_SHORT}`),
      sdkUrl: `${ORConfig.SDK_API_URL}`,
      allowIframe: Boolean(ORConfig.ALLOW_IFRAME),
    });

    return $global.$ORAuthAPI;
  };

  const getStore = () => {
    if (!_.isEmpty($global.$ORAuthStore)) {
      return $global.$ORAuthStore;
    }

    /* TODO: "@onereach/auth-ui-module" has hardcoded "@or-sdk/auth" version */
    $global.$ORAuthStore = createAuthStore(getAPIInstance() as any)();
    return $global.$ORAuthStore;
  };

  return { getAPIInstance, getStore, getRefs: () => storeToRefs(getStore()) };
};
